import api from '../../api';
import { useSnackbar } from 'notistack';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../types/User';
import LoginDialog from '../../components/Login/LoginDialog';

type LoginContextType = {
    isLoggedIn: boolean;
    user: User | null;
    token: string | null;
    login: (user: User, token: string) => void;
    logout: () => void;
    openLoginDialog: () => void;
    closeLoginDialog: () => void;
}

const LoginContext = createContext<LoginContextType | undefined>(undefined);

export const LoginProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(() => {
        const savedUser = localStorage.getItem('user');
        return savedUser ? JSON.parse(savedUser) : null;
    });
    const [token, setToken] = useState<string | null>(() => {
        return localStorage.getItem('token');
    });
    const [logined, setLogined] = useState(false);

    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [isOpenLoginDialog, setIsOpenLoginDialog] = useState(false);

    const login = useCallback((newUser: User, newToken: string) => {
        setUser(newUser);
        setToken(newToken);
        localStorage.setItem('user', JSON.stringify(newUser));
        localStorage.setItem('token', newToken);
    }, []);

    const logout = useCallback((error?: string) => {
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        navigate('/');
        if (error)
            enqueueSnackbar(error, { variant: 'error' });
        else
            enqueueSnackbar('Logged out successfully', { variant: 'success' });
    }, [navigate, enqueueSnackbar]);

    useEffect(() => {
        if (!token || !user)
            return;

        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Apply interceptors to your instance
        const requestInterceptor = api.interceptors.request.use(
            (config) => {
                config.headers.Authorization = `Bearer ${token}`;
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        const responseInterceptor = api.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response?.status === 401)
                    logout(error.response.data ?? "Session Expired");
                if (error.response?.status === 403)
                    enqueueSnackbar(error.response.data ?? "Access Denied", { variant: 'error' });
                return Promise.reject(error);
            }
        );

        setLogined(true);
        return () => {
            api.interceptors.request.eject(requestInterceptor);
            api.interceptors.response.eject(responseInterceptor);
            setLogined(false);
        };
    }, [logout, token, user, enqueueSnackbar]);

    return (
        <LoginContext.Provider
            value={{
                isLoggedIn: logined && !!user && !!token,
                user: logined ? user : null,
                token: logined ? token : null,
                login,
                logout,
                openLoginDialog: () => setIsOpenLoginDialog(true),
                closeLoginDialog: () => setIsOpenLoginDialog(false),
            }}
        >
            {children}
            <LoginDialog open={isOpenLoginDialog} onClose={() => setIsOpenLoginDialog(false)} />
        </LoginContext.Provider>
    );
};

export const useLogin = () => {
    const context = useContext(LoginContext);
    if (!context) {
        throw new Error('useLogin must be used within a LoginProvider');
    }
    return context;
};